/**
 * Navbar Component - Landing Page
 * Sticky navigation with language toggle and responsive menu
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BRANDING from '../../../../config/branding';
import { Button } from '../../../../design-system';
import { ROUTES } from '../../../../config/routes';

const Navbar = ({ language, toggleLanguage }) => {
  const isArabic = language === 'ar';
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = isArabic
    ? [
        { label: 'الرئيسية', href: '#home' },
        { label: 'المميزات', href: '#features' },
        { label: 'الأسعار', href: '#pricing' },
        { label: 'API', href: ROUTES.API_DOCS },
        { label: 'تواصل معنا', href: '#contact' },
      ]
    : [
        { label: 'Home', href: '#home' },
        { label: 'Features', href: '#features' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'API Docs', href: ROUTES.API_DOCS },
        { label: 'Contact', href: '#contact' },
      ];

  const handleMenuToggle = () => setMenuOpen((open) => !open);
  const handleNavClick = () => setMenuOpen(false);

  return (
    <nav className="sticky top-0 z-40 w-full bg-[#0B0E14]/95 backdrop-blur-md border-b border-[#2D3748] transition-all duration-300">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <div
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          onClick={() => navigate(ROUTES.HOME)}
        >
          <img
            src={BRANDING.favicon}
            alt="logo"
            className="w-8 h-8 sm:w-10 sm:h-10"
          />
          <span className="font-bold text-white text-base sm:text-lg">
            {BRANDING[language]}
          </span>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-[#A0AEC0] hover:text-white transition-colors duration-200 text-sm font-medium"
            >
              {item.label}
            </a>
          ))}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-[#A0AEC0] hover:text-white hover:bg-[#1A1D23] transition-all duration-200"
          >
            {isArabic ? 'EN' : 'AR'}
          </button>

          {/* Login Button (Desktop) */}
          <Button
            variant="secondary"
            size="sm"
            className="hidden md:block"
            onClick={() => navigate(ROUTES.LOGIN)}
          >
            {isArabic ? 'تسجيل الدخول' : 'Login'}
          </Button>

          {/* Mobile Menu Button */}
          <button
            onClick={handleMenuToggle}
            className="md:hidden flex flex-col gap-1.5 p-1"
            aria-label="Toggle menu"
          >
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? 'rotate-45 translate-y-2' : ''
              }`}
            />
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`w-5 h-0.5 bg-white transition-all duration-300 ${
                menuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-[#2D3748] bg-[#1A1D23] px-3 sm:px-4 py-3 sm:py-4 max-h-screen overflow-y-auto">
          <div className="flex flex-col gap-3 sm:gap-4">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-[#A0AEC0] hover:text-white transition-colors px-2 py-2 rounded text-sm"
                onClick={handleNavClick}
              >
                {item.label}
              </a>
            ))}
            <Button
              variant="primary"
              size="md"
              className="w-full"
              onClick={() => {
                navigate(ROUTES.LOGIN);
                handleNavClick();
              }}
            >
              {isArabic ? 'تسجيل الدخول' : 'Login'}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
